/**
 * Backend selection. `APP_BACKEND=local` (default) is the browser demo: IndexedDB + mock adapters, exactly as before.
 * `APP_BACKEND=supabase` swaps in src/adapters/supabase/*. The choice is made at BUILD time (scripts/build.mjs `define`s the
 * __APP_* constants from the environment), so the unused backend is tree-shaken out and the default bundle does not grow.
 *
 *   APP_BACKEND=supabase SUPABASE_URL=https://xxxx.supabase.co SUPABASE_ANON_KEY=eyJ... APP_URL_MODE=path npm run build
 *
 * The anon key is public by design (it only grants what RLS and the function grants allow); the service_role key must never appear here.
 */
declare const __APP_BACKEND__: string | undefined;
declare const __SUPABASE_URL__: string | undefined;
declare const __SUPABASE_ANON_KEY__: string | undefined;
declare const __APP_URL_MODE__: string | undefined;
declare const __APP_BASE_URL__: string | undefined;

export type Backend = 'local' | 'supabase';
export type UrlMode = 'hash' | 'path';

export const BUILD_BACKEND: Backend = typeof __APP_BACKEND__ !== 'undefined' && __APP_BACKEND__ === 'supabase' ? 'supabase' : 'local';
/** `path` = https://planella.co.il/b/slug (History API). `hash` = #/b/slug (default; also what every existing link uses). */
export const BUILD_URL_MODE: UrlMode = typeof __APP_URL_MODE__ !== 'undefined' && __APP_URL_MODE__ === 'path' ? 'path' : 'hash';
export const BUILD_BASE_URL: string | undefined = typeof __APP_BASE_URL__ !== 'undefined' && __APP_BASE_URL__ ? __APP_BASE_URL__ : undefined;

export interface SupabasePublicConfig { url: string; anonKey: string }
export function buildSupabaseConfig(): SupabasePublicConfig {
  const url = typeof __SUPABASE_URL__ !== 'undefined' ? __SUPABASE_URL__ : undefined;
  const anonKey = typeof __SUPABASE_ANON_KEY__ !== 'undefined' ? __SUPABASE_ANON_KEY__ : undefined;
  if (!url || !anonKey) throw new Error('APP_BACKEND=supabase needs SUPABASE_URL and SUPABASE_ANON_KEY at build time (see server/.env.example)');
  if (/service_role/i.test(anonKey)) throw new Error('refusing to build: the key looks like a service_role key');
  return { url, anonKey };
}

/** Pure parser used by tests and by tooling that reads a process environment. */
export function parseBackendEnv(env: Record<string, string | undefined>): { backend: Backend; urlMode: UrlMode; supabase?: SupabasePublicConfig } {
  const backend: Backend = env.APP_BACKEND === 'supabase' ? 'supabase' : 'local';
  const urlMode: UrlMode = env.APP_URL_MODE === 'path' ? 'path' : 'hash';
  if (backend === 'local') return { backend, urlMode };
  const url = env.SUPABASE_URL, anonKey = env.SUPABASE_ANON_KEY;
  if (!url || !/^https:\/\/[^/]+/.test(url)) throw new Error('SUPABASE_URL must be an https URL');
  if (!anonKey) throw new Error('SUPABASE_ANON_KEY is required');
  if (anonKey.split('.').length === 3) {
    try {
      const role = (JSON.parse(atob(anonKey.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { role?: string }).role;
      if (role && role !== 'anon') throw new Error(`SUPABASE_ANON_KEY has role "${role}": only the anon key may be bundled into the app`);
    } catch (e) { if (e instanceof Error && e.message.startsWith('SUPABASE_ANON_KEY has role')) throw e; }
  }
  return { backend, urlMode, supabase: { url, anonKey } };
}
